// © Microsoft Corporation. All rights reserved.

import { PerfTimeline, tryGetPerfTimeline } from "./PerfTimeline";

import { AppTraceLevel } from "./AppPerfTraceConfig";
import { getAppPerfTraceConfig } from "./ExperienceDiagnostics";
import { incrementCounter } from "./PageTimings";
import { rafTimer } from "./RAFTimer";
import { uniqueId } from "lodash-es";

const typeInstanceCounts: { [componentType: string]: number } = {};
const observerMap: WeakMap<Node, MutationObserver> = new WeakMap();

/**
 * Enables verbose perf tracking for all web-component elements
 * if app `high` app trace level is enabled.
 */
export function initWebComponentPerfTracker() {

    const { enablePerfTimeline, traceLevel } = getAppPerfTraceConfig();

    if (!enablePerfTimeline || typeof customElements === "undefined" || traceLevel < AppTraceLevel.high) {
        return;
    }

    // replace the {CustomElementRegistry}'s `define` method to intercept all
    // element registrations so we can spy on the connectedCallback for each WC.
    const _customElementsDefine = customElements.define;
    customElements.define = function (...args) {

        const [_, ComponentCtor] = args;
        const connectedCallbackName = "connectedCallback";
        const connectedCallback: Function = ComponentCtor.prototype[connectedCallbackName];

        // Only track this component if:
        // the `connectedCallback` is available (i.e., it's a custom web-component),
        // AND the `getExperienceType` method is not present (i.e., this is not a WCE)
        if (typeof connectedCallback === "function"
            && !isWCE(ComponentCtor.prototype)) {

            // replace the original connected callback with the wrapped/instrumented function
            ComponentCtor.prototype[connectedCallbackName] = function (...args) {

                // since we already track more advanced timelines inside WCEs, we only want to
                // instrument non-WCE WCs.
                const perfTimeline = tryCreateWCInstancePerfTimeline(
                    // Note that in PROD mode fallback to node name since constructor name gets minified.
                    (process.env.NODE_ENV !== "production") ? ComponentCtor.name : this.localName,
                    this.instanceId
                );

                const initEndMeasure = perfTimeline && perfTimeline.startMeasure("init").endMeasure;

                // invoke the actual connectedConnected callback
                const result = connectedCallback.apply(this, args);
                initEndMeasure && initEndMeasure();

                // track any sub-tree mutations of this component (similar to WCEs)
                trackSubtreeMutations(this, perfTimeline);

                // track the total WC instances -- note that since we only execute this block of code
                // in app trace scenarios, this count will not be available in normal PROD scenario.
                incrementCounter("WC.Count");

                return result;
            };
        }

        return _customElementsDefine.apply(customElements, args);
    };
}

/**
 * Helper to create a {PerfTimeline} object for a given WCE instance. This helper will return
 * a unique {PerfTimeline} instance with an appropriate name for tracking individual instances of WCEs
 * even if the instance ID of the WCE is the same across different instances.
 * @param componentType - The exerience type for the WCE. Used to keep track of the number of
 *  instances of that experience to form the unique ID suffix.
 * @param instanceId - The instance ID of the WCE. If multiple instances of WCEs exist with the same
 *  instanceId value, a unique ID will be assigned for each subsequent instance.
 * @returns A new {PerfTimeline} object to track performance for the given WCE instance.
 */
export function tryCreateWCInstancePerfTimeline(componentType: string, instanceId: string): PerfTimeline | null {
    // Note that we create a unique ID here for the perf timeline since we can't really tie-in the
    // bundle/script loading timeline of a specific web-component *type* to the timeline of a specific
    // web-component *instance* due to how the registration and instantiation are not coupled together.
    // Note however for single instance experiences, we make this work by using the original instanceId
    // (which the loader also uses) for the very first instance, so at least the timeline is tied-in
    // for single-instanced experiences. For multi-instance experiences, each subsequent instance will get
    // it's own timeline, and will not contain the bundle/config phases by-design.
    const typeInstanceCount = typeInstanceCounts[componentType] = (typeInstanceCounts[componentType] || 0) + 1;
    const uniqueInstanceId = typeInstanceCount === 1 ? instanceId : uniqueId(instanceId);
    return tryGetPerfTimeline(componentType, uniqueInstanceId);
}

/**
 * Helper to track mutations of a WCE node so any updates will be logged and reflected in the
 * provided {PerfTimeline} in app tracing scenarios. Please note that this helper will monitor
 * mutations of all sub-children including other custom elements, but will exclude tracking of
 * children that are other WCEs. The expectation of this helper to help track all DOM updates inside
 * a given WCE, but let other child WCEs track their own mutation under their own perf timeline object.
 * This helper is not mean to be consumed directly, but rather via the {WebComponentExperience} base class.
 * Note that since mutations do not normally cross the shadow host boundaries, this helper will recursively
 * monitor any custom elements in the sub-tree via their own {MutationObserver} instances.
 * @param node - The DOM node reference of the custom WCE to start tracking for DOM updates.
 * @param perfTimeline - The {PerfTimeline} instance of the WCE to log markers against.
 * @param frameHelper - A helper function that will return a promise that resolves when the current pending/queued
 *  work is completed. This will be used for marking the rendering timeline after DOM mutations.
 */
export function trackSubtreeMutations(
    node: Element,
    perfTimeline: PerfTimeline,
    frameHelper?: () => Promise<void>
): void {
    trackSubtreeMutationsCore(
        node,
        perfTimeline,
        getAppPerfTraceConfig().traceLevel,
        new AggregateMeasure("render", perfTimeline),
        frameHelper || runDomUpdate
    );
}

/**
 * Core helper to track mutations of a given WCE's shadow root.
 * @param node - The WCE node to track mutations for
 * @param perfTimeline - The {PerfTimeline} object to use for logging perf markers in app trace scenario
 * @param traceLevel - The app travel logging level. The `high` app trace logging level will recursively
 *  monitor mutations on the entire sub-tree (excluding other WCE nodes)
 * @param aggregateMeasure - The helper to aggregate multiple async mutation updates under a single measurement.
 *  With this helper, we can plot multiple overlapping ones as a single measurement that spans all
 * the smaller measurements.
 * @param frameHelper - A helper function that will return a promise that resolves when the current pending/queued
 *  work is completed. This will be used for marking the rendering timeline after DOM mutations.
 */
function trackSubtreeMutationsCore(
    node: Element,
    perfTimeline: PerfTimeline,
    traceLevel: AppTraceLevel,
    aggregateMeasure: AggregateMeasure,
    frameHelper: () => Promise<void>
): void {

    if (!node || !node.shadowRoot || !perfTimeline) {
        return;
    }

    // track the first-level render update through an immediate runShadowDomUpdate callback.
    aggregateMeasure.tryStartMeasure();
    frameHelper().then(() => aggregateMeasure.tryEndMeasure());

    // if trace level is default (i.e., 0), then nothing more to do. We don't want to
    // incur the overhead of the mutation observer in this case.
    if (!traceLevel) {
        return;
    }

    let observer = observerMap.get(node);

    // if we're already observing this node, then don't start another observer
    if (observer) {
        return;
    }

    observer = new MutationObserver((mutations: MutationRecord[]) => {

        aggregateMeasure.tryStartMeasure();

        // aggregate debug info of the mutation so we can log this info under the custom
        // data in the app trace. Using this, when one clicks on the app trace `render` measurement
        // all the mutation details will appear in the bottom pane and give more insight into what
        // changed during a specific mutation operation.
        const mutationNodeInfo = mutations.reduce(
            (info, mutation) => {
                mutation.addedNodes.forEach(node => info.added.push(node.nodeName));
                mutation.removedNodes.forEach(node => info.removed.push(node.nodeName));
                mutation.attributeName && info.attrs.push(mutation.attributeName);
                return info;
            },
            { added: [], removed: [], attrs: [] }
        );

        mutationNodeInfo.added.length && aggregateMeasure.addCustomData("added: " + mutationNodeInfo.added.join(","));
        mutationNodeInfo.removed.length && aggregateMeasure.addCustomData("removed: " + mutationNodeInfo.removed.join(","));
        mutationNodeInfo.attrs.length && aggregateMeasure.addCustomData("attrs: " + mutationNodeInfo.attrs.join(","));

        // schedule a RAF callback so that we can log our current tracking mutation operation as completed
        // when the next frame arrives.
        frameHelper().then(() =>
            aggregateMeasure.tryEndMeasure()
        );
    });

    observer.observe(node.shadowRoot, { childList: true, subtree: true, attributes: true });
    observer.observe(node, { attributes: true });
    observerMap.set(node, observer);
}

/**
 * Helper class to encapsulate the logic of tracking multiple asynchronous measurements
 * to consolidate all measurements within the first tryStartMeasure and first tryEndMeasure
 * into a single spanning measurement. This helper is used to consolidate multiple mutation
 * observer measurements under a given DOM tree under a single measurement instead of many
 * small measurements, which would add lots of noise to WCEs with many elements.
 */
class AggregateMeasure {

    /** The current {PerfTimeline.endMeasure} in context */
    private coreEndMeasure?: () => void;

    /** Tracks custom data sent by the caller to use to track custom data to the perf timeline */
    private customData: any[] = [];

    /**
     * Constructor
     * @param measureName - The name of the measurement that is tracked under this aggregate measure
     * @param perfTimeline - The {PerfTimeline} object to use to track measurements against.
     */
    constructor(
        private readonly measureName: string,
        private readonly perfTimeline: PerfTimeline) {
    }

    /**
     * Attempts to start a new measurement, unless one has already started.
     */
    public tryStartMeasure(): void {
        if (!this.coreEndMeasure) {
            const endMeasure = this.perfTimeline.startMeasure(this.measureName).endMeasure;
            this.coreEndMeasure = () => endMeasure(undefined, { customData: this.customData.join("\n") });
        }
    }

    /**
     * Helper to add one or more custom data between a start/end measure operation.
     * @param customData - Some arbitrary value to output as custom data to the app trace
     */
    public addCustomData(customData: string): void {
        this.customData.push(customData);
    }

    /**
     * Attempts to end the measurement that has already started via a previous tryStartMeasure call.
     * If tryEndMeasure was already called, nothing will happen as only the cycle starts with the first
     * tryStartMeasure and ends with the first tryEndMeasure.
     * @returns
     */
    public tryEndMeasure(): void {
        if (!this.coreEndMeasure) {
            return;
        }

        this.coreEndMeasure();

        // reset state and prepare for next measure
        this.customData.length = 0;
        this.coreEndMeasure = null;
    }
}

/**
 * Helper to check if a given HTML node is a custom element (i.e. WC)
 * @param node - The DOM node to check
 * @returns {boolean} indicating whether it's a custom element or not
 */
function isCustomElement(node: Node): boolean {
    return !!node.nodeName.match(/-/);
}

/**
 * Helper to check if a given HTML node is a web component experience (i.e., WCE)
 * @param node - The DOM node to check
 * @returns {boolean} indicating whether it's a WCE or not.
 */
function isWCE(node: Node): boolean {
    return ("getExperienceType" in node);
}

/**
 * Last created runDomUpdate promise that will resolve when the next rAF is invoked.
 */
let lastDomUpdate: Promise<void>;

/**
 * Async helper that will resolve with the next rAF update.
 * @returns {Promise<void>} that will resolve in the rAF update.
 */
function runDomUpdate(): Promise<void> {
    return lastDomUpdate || (lastDomUpdate = new Promise((resolve) =>
        rafTimer(() => {
            lastDomUpdate = null;
            resolve();
        })
    ));
}